import { memo } from "react";
import QCMEleve from "./QCMEleve";
import QCMMultiEleve from "./QCMMultiEleve";
import TexteLibreEleve from "./TexteLibreEleve";
import RelierEleve from "./RelierEleve";
import OrdonnerEleve from "./OrdonnerEleve";
import TexteTrouEleve from "./TexteTrouEleve";
import ClasserEleve from "./ClasserEleve";
import NumberLineInteractiveEleve from "./NumberLineInteractiveEleve";
import ProgrammeConstructionEleve from "./ProgrammeConstructionEleve";
import HorlogeAiguillesEleve from "./HorlogeAiguillesEleve";
import Repere2DInteractiveEleve from "./Repere2DInteractiveEleve";

// Référence STABLE pour les reliers sans leurres (fix 2026-08-01) : un
// `|| []` inline fabriquait un tableau neuf à chaque rendu → le useMemo du
// mélange de RelierEleve rejouait à chaque re-rendu du parent, et la colonne
// de droite se re-mélangeait sous les yeux de l'élève au moment précis où il
// reliait la dernière paire (le piège « objets par défaut dans le render »
// documenté au CLAUDE.md).
const AUCUN_LEURRE = [];

/**
 * Routeur de composants interactifs côté élève.
 * Reçoit le contenu unifié et route vers le bon composant.
 *
 * CONTRAT DE MONTAGE : les composants interactifs lisent `selectedValue`
 * UNE FOIS au montage (init useState) — aucune resynchronisation en cours de
 * vie. Toute surface qui monte QuestionInteractive doit donc fournir une
 * `key` d'identité de question (Quiz : `${part}-${index}` ; ExerciseurPreview :
 * compteur de génération) pour forcer un remontage à chaque changement.
 *
 * @param {string} typeQuestion - 'qcm' | 'texte' | 'association'
 * @param {Object} contenu - Le contenu unifié (format buildPreviewContenu)
 * @param {string} selectedValue - La réponse déjà sélectionnée (ou null)
 * @param {Function} onAnswer - Callback (valeur, imageUrl?) → stocke la réponse
 * @param {Function} onComplete - Callback (bool) → pilote le bouton Suivant
 * @param {string} radioName - Nom unique pour les radio buttons QCM
 * @param {Object} tableConfig - Config du tableau si question tableau (géré par Quiz.jsx)
 * @param {?{bonneReponse:string, reponseEleve:string}} [corrige=null] - Correction
 *   SUR PLACE (2026-09-21). Absente pendant que l'élève répond : chaque widget
 *   se comporte alors au bit près comme avant, et la bonne réponse n'entre
 *   jamais dans le DOM (invariant testé). Présente, le widget marque ses
 *   éléments avec les trois états de `answerTheme` (juste / faux / attendu)
 *   et passe en lecture seule. Transmise telle quelle à tous les widgets : ceux
 *   qui ne la connaissent pas encore l'ignorent.
 * @param {boolean} [expression=false] - La saisie est une expression
 *   mathématique (comparaisonExpression(question), calculé par l'écran qui
 *   monte le composant) : le champ affiche × quand l'élève tape *.
 */
const QuestionInteractive = memo(function QuestionInteractive({
    typeQuestion,
    contenu,
    selectedValue,
    onAnswer,
    onComplete,
    radioName,
    tableConfig,
    onAbandon,
    corrige = null,
    expression = false,
}) {
    if (!contenu) return null;

    // ─── QCM (simple ou multi) ───
    if (typeQuestion === 'qcm' || typeQuestion === 'qcm_multiple') {
        // Tableau géré séparément par Quiz.jsx via TableResponseElementEleve
        if (tableConfig) return null;
        const choix = contenu.choix || [];
        if (choix.length === 0) return null;

        // QCM multi-réponses → checkboxes
        if (contenu.multi) {
            return (
                <QCMMultiEleve
                    choix={choix}
                    selectedValue={selectedValue}
                    corrige={corrige}
                    onAnswer={onAnswer}
                    onComplete={onComplete}
                />
            );
        }

        // QCM simple → radio buttons
        return (
            <QCMEleve
                choix={choix}
                selectedValue={selectedValue}
                corrige={corrige}
                onAnswer={onAnswer}
                onComplete={onComplete}
                radioName={radioName}
            />
        );
    }

    // ─── Texte libre ───
    if (typeQuestion === 'texte') {
        return (
            <TexteLibreEleve
                selectedValue={selectedValue}
                corrige={corrige}
                onAnswer={(val) => onAnswer(val, null)}
                onComplete={onComplete}
                suffixe={contenu.suffixe}
                expression={expression}
            />
        );
    }

    // ─── Association — routing par sous-type ───
    if (typeQuestion === 'association') {
        const subType = contenu.subType;

        if (subType === 'relier') {
            return (
                <RelierEleve
                    pairs={contenu.pairs || []}
                    ciblesLeurres={contenu.cibles_leurres || AUCUN_LEURRE}
                    selectedValue={selectedValue}
                    corrige={corrige}
                    onAnswer={(val) => onAnswer(val, null)}
                    onComplete={onComplete}
                />
            );
        }

        if (subType === 'ordonner') {
            return (
                <OrdonnerEleve
                    items={contenu.items || []}
                    selectedValue={selectedValue}
                    corrige={corrige}
                    onAnswer={(val) => onAnswer(val, null)}
                    onComplete={onComplete}
                />
            );
        }

        if (subType === 'texte_trou') {
            return (
                <TexteTrouEleve
                    phrase={contenu.phrase}
                    mode={contenu.mode}
                    distracteurs={contenu.distracteurs}
                    selectedValue={selectedValue}
                    corrige={corrige}
                    onAnswer={(val) => onAnswer(val, null)}
                    onComplete={onComplete}
                />
            );
        }

        if (subType === 'classer') {
            return (
                <ClasserEleve
                    categories={contenu.categories || []}
                    selectedValue={selectedValue}
                    corrige={corrige}
                    onAnswer={(val) => onAnswer(val, null)}
                    onComplete={onComplete}
                />
            );
        }

        if (subType === 'placer_points') {
            return (
                <NumberLineInteractiveEleve
                    numberline={contenu.numberline}
                    pointsToPlace={contenu.pointsToPlace || []}
                    selectedValue={selectedValue}
                    corrige={corrige}
                    onAnswer={(val) => onAnswer(val, null)}
                    onComplete={onComplete}
                />
            );
        }

        if (subType === 'placer_points_2d') {
            return (
                <Repere2DInteractiveEleve
                    repere={contenu.repere}
                    pointsToPlace={contenu.pointsToPlace || []}
                    dejaPlaces={contenu.dejaPlaces || []}
                    masquerCoordonnees={contenu.masquerCoordonnees === true}
                    decor={contenu.decor || null}
                    relier={Array.isArray(contenu.relier) ? contenu.relier : null}
                    selectedValue={selectedValue}
                    corrige={corrige}
                    onAnswer={(val) => onAnswer(val, null)}
                    onComplete={onComplete}
                />
            );
        }

        if (subType === 'placer_aiguilles') {
            return (
                <HorlogeAiguillesEleve
                    avecTrotteuse={!!contenu.avecTrotteuse}
                    bonneReponse={contenu.bonne_reponse}
                    selectedValue={selectedValue}
                    corrige={corrige}
                    onAnswer={(val) => onAnswer(val, null)}
                    onComplete={onComplete}
                    onAbandon={onAbandon}
                />
            );
        }

        if (subType === 'programme_construction') {
            return (
                <ProgrammeConstructionEleve
                    viewBox={contenu.viewBox}
                    points={contenu.points || []}
                    preShapes={contenu.preShapes || []}
                    tasks={contenu.tasks || []}
                    tolerance={contenu.tolerance}
                    pxPerCm={contenu.pxPerCm}
                    unite={contenu.unite || 'cm'}
                    bonneReponse={contenu.bonne_reponse}
                    selectedValue={selectedValue}
                    corrige={corrige}
                    onAnswer={(val) => onAnswer(val, null)}
                    onComplete={onComplete}
                    onAbandon={onAbandon}
                />
            );
        }
    }

    return null;
});

export default QuestionInteractive;
